import type { Boundaries, EmbeddedStudioSafetyRules } from "@mountaya/client";
import { EFFECTIVE_MIN_ZOOM_FLOOR, TILE_ZOOM_MAX } from "@mountaya/elements/geo";

import {
  bodyRefusals,
  type ConsoleOrganization,
  type OrganizationBoundaryEntry,
} from "@mountayaapp/api";

/**
 * DEFAULT_ZOOM_MIN / DEFAULT_ZOOM_MAX seed the zoom range inputs when an organization
 * carries no safety rules yet. They mirror the backend create-default so the empty-state
 * inputs preview what a new organization actually gets, and stay inside the span the embed
 * supports: the default min (8) sits above the tile floor, below which there are no tiles,
 * and the default max is the tile ceiling (TILE_ZOOM_MAX), above which the client only
 * overzooms a blurry deepest tile.
 */
export const DEFAULT_ZOOM_MIN = 8;
export const DEFAULT_ZOOM_MAX = TILE_ZOOM_MAX;

/**
 * MAX_DOMAINS / MAX_OVERLAY_SLUGS are the ceilings the contract declares on the two
 * safety-rule allow-lists, restated here because a Mantine `TagsInput` takes a number and
 * the generated types carry `maxItems` in no form TypeScript can read.
 *
 * They exist so the form refuses the thirty-first entry rather than the API doing it: the
 * origin allow-list is walked on every publishable-key request, so its length is per-request
 * work forever, and a caller who only learns that from a 400 has already lost the edit.
 * `securityRules.test.ts` pins both against `EmbeddedStudioSafetyRules.yaml`, so the contract
 * stays the one place the number is decided.
 */
export const MAX_DOMAINS = 30;
export const MAX_OVERLAY_SLUGS = 64;

/**
 * MAX_BOUNDARIES is the ceiling the contract declares on the named geographic boundaries, which
 * the boundaries card holds by withholding its add once the list is full, so the fifty-first
 * region is refused before it is drawn rather than by a 400 after. `securityRules.test.ts` pins it
 * against `EmbeddedStudioSafetyRules.yaml` with the two above.
 */
export const MAX_BOUNDARIES = 50;

/**
 * LoadedSafetyRules is the organization's parsed safety-rules slice as it arrives on the org
 * read. Partial because an org with no stored rules yet reads as an empty object.
 */
export type LoadedSafetyRules = Partial<
  NonNullable<ConsoleOrganization["settings"]["safety_rules"]>
>;

/**
 * storedZoomMin / storedZoomMax read the safety zoom bounds as they are stored, or the
 * create-defaults when none is stored yet. They are read verbatim: the contract and the API take
 * any whole zoom from 0 to 22, and the Tile API enforces the stored bounds, so a value outside the
 * span the hosted embed's camera uses (an integrator drawing Mountaya tiles on its own world view
 * at z0, say) is a real rule, which no read may move and no save that did not edit it may rewrite.
 */
export const storedZoomMin = (rules: LoadedSafetyRules): number =>
  rules.zoom_min ?? DEFAULT_ZOOM_MIN;
export const storedZoomMax = (rules: LoadedSafetyRules): number =>
  rules.zoom_max ?? DEFAULT_ZOOM_MAX;

/**
 * zoomOutsideCamera reports whether a stored zoom range reaches past the span the hosted embed's
 * camera uses, [EFFECTIVE_MIN_ZOOM_FLOOR, TILE_ZOOM_MAX]: the tiles are served there, and the
 * embed simply never asks for them. The page says so beside the range rather than hiding it.
 */
export const zoomOutsideCamera = (zoomMin: number, zoomMax: number): boolean =>
  zoomMin < EFFECTIVE_MIN_ZOOM_FLOOR || zoomMax > TILE_ZOOM_MAX;

/**
 * zoomInputBounds is the range a zoom input accepts: the span the hosted embed's camera uses,
 * widened to take in the stored value, so a range stored outside it is shown as stored and can be
 * kept, while what a person types is held to the camera's span.
 */
export const zoomInputBounds = (stored: number): { max: number; min: number } => ({
  max: Math.max(TILE_ZOOM_MAX, stored),
  min: Math.min(EFFECTIVE_MIN_ZOOM_FLOOR, stored),
});

/**
 * sameStringList reports whether two string lists are element-wise equal, used to tell a
 * card whether its allow-list slice is dirty against the loaded rules.
 */
export const sameStringList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

/**
 * loadedRules reads the organization's parsed safety rules, or an empty object when none is
 * stored yet, so the slice seeds and the merge builder both work off one accessor. It reads the
 * settings alone, so a save hands it the settings its write is made on as readily as a card hands
 * it the organization it renders.
 */
export const loadedRules = (
  organization: Pick<ConsoleOrganization, "settings">,
): LoadedSafetyRules => organization.settings.safety_rules ?? {};

/**
 * readableBoundaries narrows the parsed boundaries list to the regions the client could
 * read, which is the only form the PATCH body can carry. It is deliberately NOT a
 * general accessor: a caller that renders or saves the list must first decide what to do
 * about the entries this drops, which is what hasUnreadableBoundary answers.
 */
export const readableBoundaries = (entries: readonly OrganizationBoundaryEntry[]): Boundaries[] =>
  entries.flatMap((entry) => (entry.status === "readable" ? [entry.boundary] : []));

/**
 * hasUnreadableBoundary reports whether the organization has a saved region the client
 * could not read. The geofence it describes is still enforced by the tile and embed
 * gates, so the page states that a restriction exists it cannot display and withholds
 * the boundary write rather than saving a list that silently omits it.
 */
export const hasUnreadableBoundary = (entries: readonly OrganizationBoundaryEntry[]): boolean =>
  entries.some((entry) => entry.status === "unreadable");

/**
 * StaffControlledRules are the safety rules an agreement fixes, as they stand: which overlays the
 * organization's embeds, share links and Embedded Studio may draw, whether an embed may switch
 * between them, and, on an organization billed by agreement, the sites that may embed it
 * (`domains` absent on a self-serve organization, whose administrators keep their own).
 *
 * Mountaya staff set them through the staff route, and an administrator's PATCH may not change
 * them: the API answers a change with 403 `organizations.settings.staff_controlled` at the
 * field's path, and applies nothing. The PATCH replaces the whole rules object and requires each
 * of them, so they are sent exactly as stored rather than left out.
 */
export interface StaffControlledRules {
  readonly domains?: readonly string[];
  readonly with_overlays: readonly string[];
  readonly with_overlays_control: boolean;
}

/**
 * staffControlledRules reads the staff-controlled rules out of the safety rules as stored, which
 * an administrator's save sends back unchanged. `stored` is the settings document read fresh just
 * before the save, not the organization the page loaded: a staff edit landing while the page is
 * open would otherwise be sent back as it was, which the API reads as a change and refuses.
 *
 * @param stored - The safety rules as the API reads them back, or undefined where none is stored.
 * @param billedByAgreement - Whether the organization is billed by agreement, which makes its
 *   `domains` staff's too.
 */
export const staffControlledRules = (
  stored: Partial<EmbeddedStudioSafetyRules> | undefined,
  billedByAgreement: boolean,
): StaffControlledRules => ({
  ...(billedByAgreement ? { domains: [...(stored?.domains ?? [])] } : {}),
  with_overlays: [...(stored?.with_overlays ?? [])],
  with_overlays_control: Boolean(stored?.with_overlays_control),
});

/**
 * buildSafetyRules assembles the full EmbeddedStudioSafetyRules the PATCH replaces by merging
 * one card's edited `override` slice onto the currently-loaded rules, then the staff-controlled
 * rules as they stand. Every wire field is required, so it fills each one from the loaded rules
 * verbatim (with the same create-defaults the empty state seeds where none is stored, and the
 * zoom bounds as stored, never pulled into the embed camera's span), then applies the override,
 * so a card that edits only its own slice preserves every other slice. The staff-controlled rules come
 * last and win over both, the overlay allowlist and switch always taken from them: an
 * administrators' card never sends a value of its own for them, so no save of theirs can change
 * one (`with_overlays` rides through verbatim, since an empty list is the forbid-all signal and
 * must never be coerced to a default).
 *
 * `boundaries` can only carry the regions the client read, because an unreadable entry
 * has no wire form to re-send. That makes any save while one exists a widening of the
 * geofence, which is why BoundariesCard withholds the boundary write in that state
 * instead of relying on this merge to be safe.
 */
export const buildSafetyRules = (
  loaded: LoadedSafetyRules,
  override: Partial<EmbeddedStudioSafetyRules>,
  staffControlled: StaffControlledRules,
): EmbeddedStudioSafetyRules => ({
  boundaries: readableBoundaries(loaded.boundaries ?? []),
  domains: loaded.domains ?? [],
  require_session_token: Boolean(loaded.require_session_token),
  with_collection_itineraries: Boolean(loaded.with_collection_itineraries),
  with_collection_zones: Boolean(loaded.with_collection_zones),
  with_profile_analysis: Boolean(loaded.with_profile_analysis),
  with_profile_elevation: Boolean(loaded.with_profile_elevation),
  zoom_max: storedZoomMax(loaded),
  zoom_min: storedZoomMin(loaded),
  ...override,
  ...(staffControlled.domains === undefined ? {} : { domains: [...staffControlled.domains] }),
  with_overlays: [...staffControlled.with_overlays],
  with_overlays_control: staffControlled.with_overlays_control,
});

/**
 * SafetyRulesAllowlist names the two lists of the safety rules the API refuses entry by entry:
 * the embedding domains, and the overlay allowlist. Each refused entry is answered with a 400 at
 * its index of the list the save sent, `["request", "body", "safety_rules", <list>, i]`.
 */
export type SafetyRulesAllowlist = "domains" | "with_overlays";

/** SAFETY_RULES_FIELD is the member of a settings write the safety rules are sent as. */
const SAFETY_RULES_FIELD = "safety_rules";

/**
 * refusedAllowlistEntries reads, off a refused settings save, the entries of one list of the
 * safety rules the API refused at their index, each once, in the order they are listed. The
 * index is of the list the save SENT, so the entry is read off `sent`: what the editor held
 * where the save sent its own list, the stored list where it sent that back. A refusal of the
 * list whole (too many entries, at no index), of the default props' own overlay allowlist, of
 * another field, and any other failure name none, and so does an index `sent` does not reach.
 *
 * @param error - What the save threw.
 * @param list - The list of the safety rules whose refused entries to read.
 * @param sent - That list as the refused save sent it.
 */
export const refusedAllowlistEntries = (
  error: unknown,
  list: SafetyRulesAllowlist,
  sent: readonly string[],
): string[] => {
  const refused = bodyRefusals(error, 400, list).flatMap((entry) => {
    const path = entry.path ?? [];
    const at = path.indexOf(list);
    const index = path[at + 1];
    if (path[at - 1] !== SAFETY_RULES_FIELD || typeof index !== "number") {
      return [];
    }

    const value = sent[index];

    return value === undefined ? [] : [value];
  });

  return [...new Set(refused)];
};
